import {
    AlignItems,
    Button,
    ButtonColor,
    ButtonType,
    Col,
    Column,
    Container,
    ContainerSize,
    Form,
    FormControlSize,
    FormControlType,
    Gutter,
    Heading,
    Row,
    Text
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";


type FormControlPageProps = {}

const FormControlPage = ({ }: FormControlPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            {/* Form Controls */}
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Form controls
            </Heading>
            <Text className="mb-4">
                Give textual form controls like <Text el="code">&lt;input&gt;</Text>s and <Text el="code">&lt;textarea&gt;</Text>s an upgrade with custom styles, sizing, focus states, and more.
            </Text>
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="exampleFormControlInput1">Email address</Form.Label>
                        <Form.Control
                            type={FormControlType.Email}
                            id="exampleFormControlInput1"
                            placeholder="name@example.com"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="exampleFormControl Textarea1">Example textarea</Form.Label>
                        <Form.Control
                            el="textarea"
                            id="exampleFormControlTextarea1"
                            rows={3}
                        />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {formControlsCode}
                </CodeHighlighter>
            </CodeSnippet>
            {/* Sizing */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Sizing
            </Heading>
            <Text className="mb-4">
                Set heights using the <Text el="code">size</Text> prop like{' '}
                <Text el="code">FormControlSize.Large</Text> and{' '}
                <Text el="code">FormControlSize.Small</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Control
                        size={FormControlSize.Large}
                        type={FormControlType.Text}
                        placeholder="Large input"
                        className="mb-3"
                    />
                    <Form.Control
                        type={FormControlType.Text}
                        placeholder="Default input"
                        className="mb-3"
                    />
                    <Form.Control
                        size={FormControlSize.Small}
                        type={FormControlType.Text}
                        placeholder="Small input"
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {sizingCode}
                </CodeHighlighter>
            </CodeSnippet>
            {/* Form Text */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Form text
            </Heading>
            <Text className="mb-3">
                Block-level or inline-level form text can be created using{' '}
                <Text el="code">Form.Text</Text> (Bootstrap’s <Text el="code">.form-text</Text>).
            </Text>
            <Text className="mb-4">
                Associate helper text with the control using <Text el="code">aria-describedby</Text> so
                assistive technologies announce it when the field is focused, as recommended in the{' '}
                <a
                    href="https://getbootstrap.com/docs/5.3/forms/form-control/#form-text"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap form controls docs
                </a>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="inputPassword5">Password</Form.Label>
                        <Form.Control
                            type={FormControlType.Password}
                            id="inputPassword5"
                            aria-describedby="passwordHelpBlock"
                        />
                        <Form.Text id="passwordHelpBlock" el="div" muted>
                            Your password must be 8-20 characters long, contain letters and numbers, and must not contain spaces, special characters, or emoji.
                        </Form.Text>
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {formTextCode}
                </CodeHighlighter>
            </CodeSnippet>
            <Text className="mt-4 mb-4">
                For block-level help under the field, set <Text el="code">el=&quot;div&quot;</Text> on{' '}
                <Text el="code">Form.Text</Text> so it matches Bootstrap’s block <Text el="code">.form-text</Text> pattern
                and picks up spacing below the input.
            </Text>
            <Text className="mb-4">
                Inline helper text can use a compact inline element (for example <Text el="code">small</Text>, the default
                for <Text el="code">Form.Text</Text>) with the same <Text el="code">form-text</Text> styling—often
                alongside the input in a horizontal row.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row gutter={Gutter.G3} alignItems={AlignItems.Center}>
                        <Column col={Col.ColAuto}>
                            <Form.Label htmlFor="inputPassword6" column>
                                Password
                            </Form.Label>
                        </Column>
                        <Column col={Col.ColAuto}>
                            <Form.Control
                                type={FormControlType.Password}
                                id="inputPassword6"
                                aria-describedby="passwordHelpInline"
                            />
                        </Column>
                        <Column col={Col.ColAuto}>
                            <Form.Text id="passwordHelpInline" muted>
                                Must be 8-20 characters long.
                            </Form.Text>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {formTextInlineCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Disabled */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Disabled
            </Heading>
            <Text className="mb-4">
                Add the <Text el="code">disabled</Text> boolean on <Text el="code">Form.Control</Text> for a grayed-out look,
                no pointer events, and no focus—matching{' '}
                <a
                    href="https://getbootstrap.com/docs/5.3/forms/form-control/#disabled"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap’s disabled inputs
                </a>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Control
                        className="mb-3"
                        type={FormControlType.Text}
                        placeholder="Disabled input"
                        aria-label="Disabled input example"
                        disabled
                    />
                    <Form.Control
                        type={FormControlType.Text}
                        defaultValue="Disabled readonly input"
                        aria-label="Disabled readonly input example"
                        disabled
                        readOnly
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {disabledCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Readonly */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Readonly
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">readOnly</Text> to prevent editing while keeping the field focusable and selectable.
                Disabled inputs cannot be focused; readonly inputs can, as described in the{' '}
                <a
                    href="https://getbootstrap.com/docs/5.3/forms/form-control/#readonly"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap readonly section
                </a>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Control
                        type={FormControlType.Text}
                        defaultValue="Readonly input here..."
                        aria-label="Readonly input example"
                        readOnly
                    />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {readonlyCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Readonly plain text */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Readonly plain text
            </Heading>
            <Text className="mb-4">
                For readonly inputs that should look like plain text, use the <Text el="code">plaintext</Text> prop on{' '}
                <Text el="code">Form.Control</Text> so Bootstrap’s <Text el="code">.form-control-plaintext</Text> replaces
                the default field chrome while keeping margins aligned with labels.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row className="mb-3">
                        <Column col={Col.ColSm2}>
                            <Form.Label htmlFor="staticEmail" column>
                                Email
                            </Form.Label>
                        </Column>
                        <Column col={Col.ColSm10}>
                            <Form.Control
                                plaintext
                                readOnly
                                type={FormControlType.Text}
                                id="staticEmail"
                                defaultValue="email@example.com"
                            />
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Column col={Col.ColSm2}>
                            <Form.Label htmlFor="inputPasswordReadonly" column>
                                Password
                            </Form.Label>
                        </Column>
                        <Column col={Col.ColSm10}>
                            <Form.Control
                                type={FormControlType.Password}
                                id="inputPasswordReadonly"
                            />
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {readonlyPlainTextCode}
                </CodeHighlighter>
            </CodeSnippet>
            <Text className="mb-4">
                You can also lay out plaintext fields inline in a row with visually hidden labels, as in Bootstrap’s
                horizontal example.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form
                        className="row g-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <Column col={Col.ColAuto}>
                            <Form.Label htmlFor="staticEmail2" visuallyHidden>
                                Email
                            </Form.Label>
                            <Form.Control
                                plaintext
                                readOnly
                                type={FormControlType.Text}
                                id="staticEmail2"
                                defaultValue="email@example.com"
                            />
                        </Column>
                        <Column col={Col.ColAuto}>
                            <Form.Label htmlFor="inputPassword2" visuallyHidden>
                                Password
                            </Form.Label>
                            <Form.Control
                                type={FormControlType.Password}
                                id="inputPassword2"
                                placeholder="Password"
                            />
                        </Column>
                        <Column col={Col.ColAuto}>
                            <Button type={ButtonType.Submit} color={ButtonColor.Primary} className="mb-3">
                                Confirm identity
                            </Button>
                        </Column>
                    </Form>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {readonlyPlainTextInlineCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* File input */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                File input
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="formFile">Default file input example</Form.Label>
                        <Form.Control type={FormControlType.File} id="formFile" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="formFileMultiple">Multiple files input example</Form.Label>
                        <Form.Control type={FormControlType.File} id="formFileMultiple" multiple />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="formFileDisabled">Disabled file input example</Form.Label>
                        <Form.Control type={FormControlType.File} id="formFileDisabled" disabled />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {fileInputCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Color */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Color
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="exampleColorInput">Color picker</Form.Label>
                        <Form.Control
                            className="form-control-color"
                            type={FormControlType.Color}
                            id="exampleColorInput"
                            defaultValue="#563d7c"
                            title="Choose your color"
                        />
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {colorCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Datalists */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Datalists
            </Heading>
            <Text className="mb-4">
                Datalists attach suggestions to a text input. Use <Text el="code">Form.Datalist</Text> with{' '}
                <Text el="code">Form.Datalist.Option</Text> (or plain <Text el="code">&lt;option&gt;</Text> children).
                The <Text el="code">list</Text> prop on <Text el="code">Form.Control</Text> must match the datalist{' '}
                <Text el="code">id</Text>. Behavior and styling vary by browser;
                see the{' '}
                <a
                    href="https://getbootstrap.com/docs/5.3/forms/form-control/#datalists"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap datalists docs
                </a>{' '}
                and{' '}
                <a
                    href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    MDN: datalist element
                </a>
                .
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form.Group>
                        <Form.Label htmlFor="exampleDataList">Datalist example</Form.Label>
                        <Form.Control
                            list="datalistOptions"
                            id="exampleDataList"
                            placeholder="Type to search..."
                        />
                        <datalist id="datalistOptions">
                            <option value="San Francisco" />
                            <option value="New York" />
                            <option value="Seattle" />
                            <option value="Los Angeles" />
                            <option value="Chicago" />
                        </datalist>
                        <Form.Datalist id="datalistOptions">
                            <Form.Datalist.Option value="San Francisco" />
                            <Form.Datalist.Option value="New York" />
                            <Form.Datalist.Option value="Seattle" />
                            <Form.Datalist.Option value="Los Angeles" />
                            <Form.Datalist.Option value="Chicago" />
                        </Form.Datalist>
                    </Form.Group>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {datalistCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

const formControlsCode = `<Form.Group className="mb-3">
  <Form.Label htmlFor="exampleFormControlInput1">Email address</Form.Label>
  <Form.Control
    type={FormControlType.Email}
    id="exampleFormControlInput1"
    placeholder="name@example.com"
  />
</Form.Group>
<Form.Group>
  <Form.Label htmlFor="exampleFormControlTextarea1">Example textarea</Form.Label>
  <Form.Control
    el="textarea"
    id="exampleFormControlTextarea1"
    rows={3}
  />
</Form.Group>`;

const sizingCode = `<Form.Control
  size={FormControlSize.Large}
  type={FormControlType.Text}
  placeholder="Large input"
/>
<Form.Control
  type={FormControlType.Text}
  placeholder="Default input"
/>
<Form.Control
  size={FormControlSize.Small}
  type={FormControlType.Text}
  placeholder="Small input"
/>`;


const formTextCode = `<Form.Group>
  <Form.Label htmlFor="inputPassword5">Password</Form.Label>
  <Form.Control
    type={FormControlType.Password}
    id="inputPassword5"
    aria-describedby="passwordHelpBlock"
  />
  <Form.Text id="passwordHelpBlock" el="div" muted>
    Your password must be 8-20 characters long, contain letters and numbers,
    and must not contain spaces, special characters, or emoji.
  </Form.Text>
</Form.Group>`;

const formTextInlineCode = `<Row gutter={Gutter.G3} alignItems={AlignItems.Center}>
  <Column col={Col.ColAuto}>
    <Form.Label htmlFor="inputPassword6" column>
      Password
    </Form.Label>
  </Column>
  <Column col={Col.ColAuto}>
    <Form.Control
      type={FormControlType.Password}
      id="inputPassword6"
      aria-describedby="passwordHelpInline"
    />
  </Column>
  <Column col={Col.ColAuto}>
    <Form.Text id="passwordHelpInline" muted>
      Must be 8-20 characters long.
    </Form.Text>
  </Column>
</Row>`;

const disabledCode = `<Form.Control
  className="mb-3"
  type={FormControlType.Text}
  placeholder="Disabled input"
  aria-label="Disabled input example"
  disabled
/>
<Form.Control
  type={FormControlType.Text}
  defaultValue="Disabled readonly input"
  aria-label="Disabled readonly input example"
  disabled
  readOnly
/>`;

const readonlyCode = `<Form.Control
  type={FormControlType.Text}
  defaultValue="Readonly input here..."
  aria-label="Readonly input example"
  readOnly
/>`;

const readonlyPlainTextCode = `<Row className="mb-3">
  <Column col={Col.ColSm2}>
    <Form.Label htmlFor="staticEmail" column>
      Email
    </Form.Label>
  </Column>
  <Column col={Col.ColSm10}>
    <Form.Control
      plaintext
      readOnly
      type={FormControlType.Text}
      id="staticEmail"
      defaultValue="email@example.com"
    />
  </Column>
</Row>
<Row className="mb-3">
  <Column col={Col.ColSm2}>
    <Form.Label htmlFor="inputPasswordReadonly" column>
      Password
    </Form.Label>
  </Column>
  <Column col={Col.ColSm10}>
    <Form.Control
      type={FormControlType.Password}
      id="inputPasswordReadonly"
    />
  </Column>
</Row>`;

const readonlyPlainTextInlineCode = `<Form
  className="row g-3"
  onSubmit={(e) => {
    e.preventDefault();
  }}
>
  <Column col={Col.ColAuto}>
    <Form.Label htmlFor="staticEmail2" visuallyHidden>
      Email
    </Form.Label>
    <Form.Control
      plaintext
      readOnly
      type={FormControlType.Text}
      id="staticEmail2"
      defaultValue="email@example.com"
    />
  </Column>
  <Column col={Col.ColAuto}>
    <Form.Label htmlFor="inputPassword2" visuallyHidden>
      Password
    </Form.Label>
    <Form.Control
      type={FormControlType.Password}
      id="inputPassword2"
      placeholder="Password"
    />
  </Column>
  <Column col={Col.ColAuto}>
    <Button type={ButtonType.Submit} color={ButtonColor.Primary} className="mb-3">
      Confirm identity
    </Button>
  </Column>
</Form>`;

const fileInputCode = `<Form.Group className="mb-3">
  <Form.Label htmlFor="formFile">Default file input example</Form.Label>
  <Form.Control type={FormControlType.File} id="formFile" />
</Form.Group>
<Form.Group className="mb-3">
  <Form.Label htmlFor="formFileMultiple">Multiple files input example</Form.Label>
  <Form.Control type={FormControlType.File} id="formFileMultiple" multiple />
</Form.Group>
<Form.Group>
  <Form.Label htmlFor="formFileDisabled">Disabled file input example</Form.Label>
  <Form.Control type={FormControlType.File} id="formFileDisabled" disabled />
</Form.Group>`;

const colorCode = `<Form.Group>
  <Form.Label htmlFor="exampleColorInput">Color picker</Form.Label>
  <Form.Control
    className="form-control-color"
    type={FormControlType.Color}
    id="exampleColorInput"
    defaultValue="#563d7c"
    title="Choose your color"
  />
</Form.Group>`;

const datalistCode = `<Form.Group>
  <Form.Label htmlFor="exampleDataList">Datalist example</Form.Label>
  <Form.Control
    list="datalistOptions"
    id="exampleDataList"
    placeholder="Type to search..."
  />
  <Form.Datalist id="datalistOptions">
    <Form.Datalist.Option value="San Francisco" />
    <Form.Datalist.Option value="New York" />
    <Form.Datalist.Option value="Seattle" />
    <Form.Datalist.Option value="Los Angeles" />
    <Form.Datalist.Option value="Chicago" />
  </Form.Datalist>
</Form.Group>`;

export default FormControlPage;
